import { mimeTypes } from 'client/constants/editorSettings'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import { useManagement, usePage } from 'client/store'
import { DataFile } from 'client/store/files/filesReducer'
import { disableInstantUpload } from 'client/store/management/managementActions'
import managementSelectors from 'client/store/management/managementSelectors'
import pageSelectors from 'client/store/page/pageSelectors'
import { ImageActionEnum } from 'common/components/entities/ImageNew'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { PageTypeEnum } from 'common/enums/PageTypeEnum'
import { ImageInterface } from 'common/types/entities/ImageInterface'
import { OldEntityInterface } from 'common/types/entities/OldEntityInterface'
import { PopupInterface } from 'common/types/entities/popup-interface'
import { FullBorderRadiusType } from 'common/types/styleTypes'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { CheckboxControl } from 'src/editor/shared/settings/checkbox-control'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { RangeElement } from 'src/editor/shared/ui-lib/range-element'
import {
  OptionSingleValueType,
  SelectNew,
} from 'src/editor/shared/ui-lib/select-new/select-new'
import { FileUpload } from '../components'
import Input from '../components/Input/Input'
import { useUpdateEntity, useUpdateProp } from '../hooks/useUpdateProps'
import { DelayOption } from '../options'
import DeviceAppearanceNew from '../options/DeviceAppearance/DeviceAppearanceNew'
import HtmlAttrId from '../options/HtmlAttrId'
import ImageSize from '../options/ImageSize'
import { FlexAlignStyle, ShadowStyle } from '../styles'
import Border from '../styles/Border'

function detectAction(entity: ImageInterface) {
  if (entity.popupId) {
    return ImageActionEnum.ShowPopup
  }

  return ImageActionEnum.OpenUrl
}

const actionNames = {
  [ImageActionEnum.ShowPopup]: 'components.image.actions.show_popup',
  [ImageActionEnum.OpenUrl]: 'components.image.actions.open_url',
}

function transformRadiusToFullBorderRadiusType(
  radius: number | undefined | FullBorderRadiusType,
) {
  if (typeof radius === 'number') {
    return {
      borderTopLeftRadius: radius,
      borderTopRightRadius: radius,
      borderBottomRightRadius: radius,
      borderBottomLeftRadius: radius,
    }
    // biome-ignore lint/style/noUselessElse: <explanation>
  } else if (typeof radius === 'undefined') {
    return {}
    // biome-ignore lint/style/noUselessElse: <explanation>
  } else return radius
}

function ImageSettingsNew({
  entity,
  defaultEntity,
}: {
  entity: ImageInterface
  defaultEntity?: ImageInterface
}) {
  const [action, setAction] = useState(detectAction(entity))
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const popups = usePage(pageSelectors.getWebsiteGlobalOrPagePopupEntities)(
    entity,
  )
  const isLecture = usePage(pageSelectors.getPageType) === PageTypeEnum.Lecture
  const { isMobile } = useDeviceModeContext()
  const updateEntity = useUpdateEntity<ImageInterface>()
  const updateProp = useUpdateProp(entity)
  const isInstantUploadEnabled = useManagement(
    managementSelectors.isInstantUploadEnabled,
  )

  function getPopupOption(popup: OldEntityInterface | PopupInterface) {
    return {
      value: popup.id,
      label:
        popup.type === EntityTypeEnum.Popup
          ? popup?.title
          : popup?.options?.title,
    }
  }
  const popupOptions = popups.map(getPopupOption)
  const selectedPopup = popups.find(popup => popup.id === entity.popupId)

  function getActionOption(action: ImageActionEnum) {
    return {
      label: t(actionNames[action]),
      value: action,
    }
  }

  function removePopupId() {
    const updatedEntity = { ...entity }
    // biome-ignore lint/performance/noDelete: <explanation>
    delete updatedEntity.popupId
    updateEntity(updatedEntity)
  }

  function updatePopupId(option: OptionSingleValueType) {
    if (option) {
      updateProp('popupId')(option.value as string)
    } else {
      removePopupId()
    }
  }

  function updateImage(file: DataFile) {
    const img = new Image()
    img.src = file.path
    img.onload = function () {
      const imageWrapper = document.getElementById(entity.id)
      if (imageWrapper) {
        const maxWidth = imageWrapper.getBoundingClientRect().width
        let resultWidth = maxWidth
        // @ts-expect-error
        if (maxWidth > this.width) {
          // @ts-expect-error
          resultWidth = this.width
        }

        updateEntity({
          ...entity,
          fileId: file.id,
          width: Number(resultWidth.toFixed(0)),
          // @ts-expect-error
          ratio: Number((this.width / this.height).toFixed(2)),
        })
      }
    }
  }

  function updateFileLink(file: DataFile) {
    updateProp('link')(file.path)
  }

  function removeLink() {
    const updatedEntity = { ...entity }
    // biome-ignore lint/performance/noDelete: <explanation>
    delete updatedEntity.link
    updateEntity(updatedEntity)
  }

  function removeFileId() {
    const updatedEntity = { ...entity }
    // biome-ignore lint/performance/noDelete: <explanation>
    delete updatedEntity.fileId
    // biome-ignore lint/performance/noDelete: <explanation>
    delete updatedEntity.externalUrl
    // biome-ignore lint/performance/noDelete: <explanation>
    delete updatedEntity.ratio
    updateEntity(updatedEntity)
  }

  function updateAction(option: OptionSingleValueType<ImageActionEnum>) {
    if (option) {
      setAction(option.value)

      if (option.value === ImageActionEnum.OpenUrl && entity.popupId) {
        removePopupId()
      } else if (option.value === ImageActionEnum.ShowPopup && entity.link) {
        removeLink()
      }
    }
  }

  function getMaxWidth() {
    const imageWrapper = document.getElementById(entity.id)
    if (!imageWrapper) {
      return
    }

    return Number(imageWrapper.getBoundingClientRect().width.toFixed(0))
  }

  function getImageRatio() {
    const image = document.getElementById(entity.htmlAttrId)
    if (!image) {
      return
    }

    const { width, height } = image.getBoundingClientRect()
    return Number((width / height).toFixed(2))
  }

  function updateImageSize(value: number | string) {
    const updatedEntity = {
      ...entity,
      width: value,
    }

    if (!entity.ratio) {
      const ratio = getImageRatio()
      if (ratio) {
        updatedEntity.ratio = ratio
      }
    }

    updateEntity(updatedEntity)
  }

  function updateImageMobileSize(value: number | string) {
    const updatedEntity = {
      ...entity,
      mobileWidth: value,
    }

    if (!entity.ratio) {
      const ratio = getImageRatio()
      if (ratio) {
        updatedEntity.ratio = ratio
      }
    }

    updateEntity(updatedEntity)
  }

  function handleRoundedCornersChange(corner: string) {
    return (cornerValue: number) => {
      if (entity.border) {
        const entityBorderRadius = transformRadiusToFullBorderRadiusType(
          entity.border.radius,
        ) as FullBorderRadiusType
        updateEntity({
          ...entity,
          border: {
            ...entity.border,
            radius: {
              ...entityBorderRadius,
              [corner]: cornerValue,
            },
          },
        })
      } else {
        entity.border = {}
        entity.border.radius = {
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          borderBottomLeftRadius: 0,
        }
        updateEntity({
          ...entity,
          border: {
            ...entity.border,
            radius: {
              ...entity.border.radius,
              [corner]: cornerValue,
            },
          },
        })
      }
    }
  }

  const optionActionImage = isLecture
    ? [getActionOption(ImageActionEnum.OpenUrl)]
    : [
        getActionOption(ImageActionEnum.OpenUrl),
        getActionOption(ImageActionEnum.ShowPopup),
      ]

  return (
    <>
      <FileUpload
        directLink={entity.externalUrl}
        changeLinkHandler={updateProp('externalUrl')}
        fileId={entity.fileId}
        afterUpload={updateImage}
        removeFile={removeFileId}
        mimeTypes={mimeTypes.image}
        fileType="image"
        readonly={false}
        label="image"
        instantUploadEnabled={isInstantUploadEnabled}
        onClose={() => dispatch(disableInstantUpload())}
      />
      <SelectNew
        selectedOption={getActionOption(action)}
        onChange={updateAction}
        labelText="entity_settings.image.choose_action"
        options={optionActionImage}
        isMulti={false}
      />
      {action === ImageActionEnum.OpenUrl && (
        <>
          {/*@ts-ignore*/}
          <FileUpload
            directLink={entity.link}
            changeLinkHandler={updateProp('link')}
            afterUpload={updateFileLink}
            removeFile={removeLink}
            mimeTypes={mimeTypes.all}
            label="entity_settings.image.link"
            readonly={false}
            placeholder={t('entity_settings.image.choose_file_or_write_url')}
          />
          {/* @ts-ignore */}
          <CheckboxControl
            label={'entity_settings.image.target_label'}
            update={updateProp('openNewTab')}
            value={entity.openNewTab}
          />
        </>
      )}
      {action === ImageActionEnum.ShowPopup && (
        <SelectNew
          labelText="entity_settings.image.popup.label"
          onChange={updatePopupId}
          options={popupOptions}
          placeholder={t('entity_settings.image.popup.first_option')}
          selectedOption={selectedPopup ? getPopupOption(selectedPopup) : null}
          isMulti={false}
        />
      )}
      <Input update={updateProp('alt')} label="alt" value={entity.alt} />
      <ImageSize
        attrId={entity.id}
        updateWidth={updateImageSize}
        updateMobileWidth={updateImageMobileSize}
        width={entity.width || getMaxWidth()}
        mobileWidth={entity.mobileWidth}
      />
      <FlexAlignStyle
        align={entity.alignSelf}
        mobileAlign={entity.mobileAlignSelf || entity.alignSelf}
        mobileUpdate={updateProp('mobileAlignSelf')}
        update={updateProp('alignSelf')}
      />
      <ColorPickerControl
        isGradientAvailable
        label="overlay color"
        color={entity.background}
        mobileColor={entity.mobileBackground || entity.background}
        update={updateProp('background')}
        mobileUpdate={updateProp('mobileBackground')}
      />
      <SizeAndPositionGroup
        margin={isMobile ? entity.mobileMargin : entity.margin}
        defaultMargin={
          isMobile ? defaultEntity?.mobileMargin : defaultEntity?.margin
        }
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <Border
        border={entity.border}
        mobileBorder={entity.mobileBorder}
        defaultBorder={defaultEntity?.border}
        mobileDefaultBorder={defaultEntity?.mobileBorder}
        update={updateProp('border')}
        mobileUpdate={updateProp('mobileBorder')}
      />
      <GroupTitle>filters</GroupTitle>
      <RangeElement
        label="blur"
        value={entity.blur}
        onChange={updateProp('blur')}
        max={10}
      />
      <ShadowStyle
        shadow={entity.boxShadow}
        mobileShadow={entity.mobileBoxShadow}
        update={updateProp('boxShadow')}
        mobileUpdate={updateProp('boxShadow')}
      />
      <DelayOption update={updateProp('delay')} delay={entity.delay} />
      <DeviceAppearanceNew entity={entity} />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}

export default ImageSettingsNew
